/**
 * api/qa.js — booking issues / NCR (incl. external workflow + comments),
 * R37 checklist stages, sampling rules + flags.
 */
import { supabase } from "@/lib/supabaseClient";
import { nullEmpty } from "@/lib/constants";

/* =====================  R37 / QA — Issues  ===================== */

export async function listBookingIssues({ bookingId = null, siteId = null, status = null, clientId = null, limit = 200 } = {}) {
  let q = supabase
    .from("lbs_booking_issues")
    .select(
      `id, booking_id, client_id, site_id, issue_type, title, body,
       sample_position, sample_temp_c, status,
       raised_by_user_id, raised_by_role, raised_at,
       closed_by_user_id, closed_at, resolution_notes, created_at,
       public_token, external_email, external_company, notified_at,
       site:lbs_sites!lbs_booking_issues_site_id_fkey(name,code),
       booking:lbs_bookings!lbs_booking_issues_booking_id_fkey(reference_number,scheduled_start)`
    )
    .order("raised_at", { ascending: false })
    .limit(limit);
  if (bookingId) q = q.eq("booking_id", bookingId);
  if (siteId)    q = q.eq("site_id", siteId);
  if (clientId)  q = q.eq("client_id", clientId);
  if (status)    q = q.eq("status", status);
  return q;
}

export async function createBookingIssue(values, currentUserId, currentRole) {
  const payload = nullEmpty({
    booking_id: values.booking_id,
    issue_type: values.issue_type,
    title: values.title,
    body: values.body,
    sample_position: values.sample_position !== "" ? parseInt(values.sample_position, 10) : null,
    sample_temp_c:   values.sample_temp_c   !== "" ? Number(values.sample_temp_c) : null,
    raised_by_user_id: currentUserId || null,
    raised_by_role: currentRole || null,
  });
  return supabase.from("lbs_booking_issues").insert(payload).select().maybeSingle();
}

export async function updateBookingIssue(id, values) {
  return supabase
    .from("lbs_booking_issues")
    .update(nullEmpty(values))
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function closeBookingIssue(id, resolutionNotes, currentUserId) {
  return supabase
    .from("lbs_booking_issues")
    .update({
      status: "closed",
      resolution_notes: resolutionNotes || null,
      closed_by_user_id: currentUserId || null,
    })
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function deleteBookingIssue(id) {
  return supabase.from("lbs_booking_issues").delete().eq("id", id).select();
}

/* =====================  NCR external workflow (Phase β)  ============= */

export async function listIssueComments(issueId) {
  return supabase
    .from("lbs_booking_issue_comments")
    .select("id, issue_id, author_user_id, author_role, author_name, author_email, author_kind, body, created_at")
    .eq("issue_id", issueId)
    .order("created_at", { ascending: true });
}

export async function addInternalIssueComment(issueId, body, user, role) {
  if (!body?.trim()) return { error: { message: "Body required" } };
  const payload = {
    issue_id: issueId,
    body: body.trim(),
    author_user_id: user?.id || null,
    author_name: user?.user_metadata?.full_name || user?.email || "internal",
    author_email: user?.email || null,
    author_role: role || null,
    author_kind: "internal",
  };
  return supabase.from("lbs_booking_issue_comments").insert(payload).select().maybeSingle();
}

export async function getPublicNcr(token) {
  return supabase.rpc("lbs_get_ncr_public", { p_token: token });
}

export async function submitPublicNcrComment(token, body, name, email) {
  return supabase.rpc("lbs_submit_ncr_external_comment", {
    p_token: token,
    p_body:  body,
    p_name:  name,
    p_email: email || "",
  });
}

export async function notifyNcrExternal(issue, booking, site) {
  if (!issue?.external_email) return { error: { message: "No external email" } };
  if (!issue?.public_token)   return { error: { message: "Issue missing public_token (run migration 041)" } };

  const base = (typeof window !== "undefined" && window.location.origin) || "";
  const ncrUrl = `${base}/p/ncr/${issue.public_token}`;

  const context = {
    reference_number: booking?.reference_number || "",
    scheduled_start:  booking?.scheduled_start  || "",
    site_name:        site?.name || "",
    issue_type:       issue.issue_type || "",
    issue_title:      issue.title || "",
    issue_body:       issue.body  || "",
    external_company: issue.external_company || "",
    ncr_resolution_url: ncrUrl,
  };

  const { data, error } = await supabase
    .from("lbs_email_outbox")
    .insert({
      to_email:     issue.external_email,
      template_key: "ncr_raised_external",
      context,
      client_id:    issue.client_id || null,
      status:       "pending",
    })
    .select()
    .maybeSingle();

  if (error) return { error };

  await supabase
    .from("lbs_booking_issues")
    .update({ notified_at: new Date().toISOString() })
    .eq("id", issue.id);

  return { data };
}

export async function markBookingAsNcr({ bookingId, title, body, externalEmail, externalCompany }, user, role) {
  const { data, error } = await supabase
    .from("lbs_booking_issues")
    .insert({
      booking_id: bookingId,
      issue_type: "ncr",
      title: title || "NCR raised",
      body:  body  || "",
      external_email:   externalEmail   || null,
      external_company: externalCompany || null,
      raised_by_user_id: user?.id || null,
      raised_by_role:    role || null,
    })
    .select(`
      id, booking_id, client_id, site_id, public_token, external_email,
      external_company, issue_type, title, body, status, raised_at,
      booking:lbs_bookings!lbs_booking_issues_booking_id_fkey(reference_number,scheduled_start),
      site:lbs_sites!lbs_booking_issues_site_id_fkey(name,code)
    `)
    .maybeSingle();
  return { data, error };
}

/* =====================  R37 / Checklist stages  ===== */

const STAGE_ORDER = {
  office_booking: 10,
  gate_check:     20,
  pre_load:       30,
  during_load:    40,
  post_load:      50,
};
const ALL_STAGES = Object.keys(STAGE_ORDER);
export { ALL_STAGES as CHECKLIST_STAGES };

export async function getBookingChecklist(bookingId) {
  const [hdr, st] = await Promise.all([
    supabase.from("lbs_booking_checklists").select("*").eq("booking_id", bookingId).maybeSingle(),
    supabase
      .from("lbs_booking_checklist_stages")
      .select("*")
      .eq("booking_id", bookingId)
      .order("stage_order", { ascending: true }),
  ]);
  return {
    header: hdr.data,
    stages: st.data || [],
    error: hdr.error || st.error,
  };
}

export async function seedBookingChecklist(bookingId) {
  return supabase.rpc("lbs_seed_booking_checklist", { p_booking_id: bookingId });
}

export async function updateChecklistStage(stageId, patch) {
  return supabase
    .from("lbs_booking_checklist_stages")
    .update(patch)
    .eq("id", stageId)
    .select()
    .maybeSingle();
}

export async function signOffChecklistStage({ stageId, status, userId, role, signatureText, notes, data }) {
  const patch = {
    status,
    completed_by_user_id: userId || null,
    completed_by_role:    role || null,
    signature_text:       signatureText || null,
    notes:                notes || null,
  };
  if (data !== undefined) patch.data = data;
  return supabase
    .from("lbs_booking_checklist_stages")
    .update(patch)
    .eq("id", stageId)
    .select()
    .maybeSingle();
}

/* =====================  SAMPLING RULES  ============== */

export const SAMPLING_SCOPES = [
  { value: "customer",       label: "Customer (sender/receiver)" },
  { value: "transport",      label: "Transport / carrier" },
  { value: "vehicle",        label: "Vehicle" },
  { value: "driver",         label: "Driver" },
  { value: "equipment",      label: "Equipment (pallet)" },
  { value: "load_restraint", label: "Load restraint rule" },
];

export const SAMPLING_TRIGGERS = [
  { value: "every_nth",       label: "Every Nth match" },
  { value: "random_percent",  label: "Random %" },
  { value: "ncr_escalation",  label: "Escalate after N NCRs" },
  { value: "always",          label: "Always sample" },
];

export async function listSamplingRules({ clientId = null, siteId = null } = {}) {
  let q = supabase
    .from("lbs_sampling_rules")
    .select(
      `id, client_id, site_id, name, scope, target_match, target_value,
       trigger_type, frequency_n, random_percent, ncr_threshold,
       ncr_window_days, is_active, notes, match_counter, created_at, updated_at,
       site:lbs_sites!lbs_sampling_rules_site_id_fkey(name,code)`
    )
    .order("created_at", { ascending: false });
  if (clientId) q = q.eq("client_id", clientId);
  if (siteId)   q = q.eq("site_id", siteId);
  return q;
}

export async function upsertSamplingRule(values) {
  const payload = nullEmpty({
    ...values,
    frequency_n:     values.frequency_n     !== "" ? parseInt(values.frequency_n, 10)     : null,
    random_percent:  values.random_percent  !== "" ? parseInt(values.random_percent, 10)  : null,
    ncr_threshold:   values.ncr_threshold   !== "" ? parseInt(values.ncr_threshold, 10)   : null,
    ncr_window_days: values.ncr_window_days !== "" ? parseInt(values.ncr_window_days, 10) : 30,
    is_active: values.is_active !== false,
  });
  if (values.id) {
    return supabase.from("lbs_sampling_rules").update(payload).eq("id", values.id).select().maybeSingle();
  }
  return supabase.from("lbs_sampling_rules").insert(payload).select().maybeSingle();
}

export async function deleteSamplingRule(id) {
  return supabase.from("lbs_sampling_rules").delete().eq("id", id).select();
}

export async function toggleSamplingRule(id, isActive) {
  return supabase.from("lbs_sampling_rules").update({ is_active: isActive }).eq("id", id).select().maybeSingle();
}

export async function listSamplingFlagsForBooking(bookingId) {
  return supabase
    .from("lbs_sampling_flags")
    .select(
      `id, booking_id, rule_id, scope, reason, status, notes,
       sampled_by_user_id, sampled_at, created_at,
       rule:lbs_sampling_rules!lbs_sampling_flags_rule_id_fkey(name,trigger_type)`
    )
    .eq("booking_id", bookingId)
    .order("created_at", { ascending: false });
}

export async function markSamplingFlag(id, status, userId, notes = null) {
  return supabase
    .from("lbs_sampling_flags")
    .update({
      status,
      sampled_by_user_id: status === "sampled" ? userId : null,
      sampled_at:         status === "sampled" ? new Date().toISOString() : null,
      notes,
    })
    .eq("id", id)
    .select()
    .maybeSingle();
}
